/**
 * Learning Center API: an admin's direct assignment actions, and the course
 * list an admin picks from when assigning or requiring a course (plan
 * docs/plans/lms-plan.md Appendix A "Assignments and campaigns", "Courses and
 * catalog"; 4.6, 8.2, 8.4, 18.16, 18.17; WP-31's and WP-30's routes, WP-43's
 * client). Typed calls over services/api; nothing under src/lms imports axios
 * (2.3a).
 *
 *   POST /admin/assignments {user_id, course_id, due_at, message?} -> { assignment, outcome }
 *        (201 on create, 200 on a merge into the open assignment, 18.16)
 *   POST /admin/assignments/:id/waive {reason}     -> assignment (R22)
 *   POST /admin/assignments/:id/unwaive {due_at?}  -> the new open assignment (the waived row stays)
 *   POST /admin/assignments/:id/cancel {reason}    -> assignment
 *   GET  /courses?all=1&category=&limit=&offset=   -> { items, page } (18.17: an lms admin sees
 *        every course of the network with its draft; a learner without all=1 sees published ones)
 *
 * The person's own list (GET /me/assignments) is read by the learner screens
 * through api/courses.ts (WP-40) and api/me.ts (WP-41). WP-45's waive and
 * unwaive in the matrix and on the person page call the functions here.
 */
import api from '../../services/api';
import type { CourseCategory, CourseKind } from '../content/types';
import type { LmsList } from './client';
import type { AssignmentStatus } from './campaigns';

const enc = encodeURIComponent;

/** services/lms/assignments/waivers.js: a waive or cancel reason, at most 500 characters. */
export const ASSIGNMENT_REASON_MAX = 500;

/** 5.4 lms_assignments.source. */
export type AdminAssignmentSource = 'profile' | 'campaign' | 'renewal' | 'retrain' | 'acknowledge' | 'manual' | 'onboarding';

/** An lms_assignments row (5.4). */
export interface LmsAssignment {
  id: string;
  user_id: string;
  course_id: string;
  network_id: string;
  site_id: string | null;
  source: AdminAssignmentSource;
  source_ref: string | null;
  required_version_id: string | null;
  mode: 'full' | 'acknowledge';
  due_at: string;
  status: AssignmentStatus;
  completed_at: string | null;
  completion_id: string | null;
  waived_by: string | null;
  waived_at: string | null;
  waive_reason: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  last_notified_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CreateAssignmentInput {
  user_id: string;
  course_id: string;
  /** An ISO instant. */
  due_at: string;
  message?: string | null;
}

export interface CreateAssignmentResult {
  assignment: LmsAssignment;
  /** 4.6: a new assignment, or merged into the person's open one (the earlier due date kept). */
  outcome: 'assigned' | 'already_assigned';
}

/** POST /api/lms/admin/assignments (one person, source manual). */
export async function createAssignment(input: CreateAssignmentInput): Promise<CreateAssignmentResult> {
  const body: Record<string, unknown> = { user_id: input.user_id, course_id: input.course_id, due_at: input.due_at };
  const message = (input.message ?? '').trim();
  if (message) body.message = message;
  const response = await api.post<CreateAssignmentResult>('/api/lms/admin/assignments', body);
  return response.data;
}

/** POST /api/lms/admin/assignments/:id/waive {reason} (R22, 8.4). */
export async function waiveAssignment(assignmentId: string, reason: string): Promise<LmsAssignment> {
  const response = await api.post<LmsAssignment>(`/api/lms/admin/assignments/${enc(assignmentId)}/waive`, { reason });
  return response.data;
}

/** POST /api/lms/admin/assignments/:id/unwaive {due_at?}: a new open assignment; the waived row stays. */
export async function unwaiveAssignment(assignmentId: string, options: { due_at?: string | null } = {}): Promise<LmsAssignment> {
  const body: Record<string, unknown> = {};
  if (options.due_at) body.due_at = options.due_at;
  const response = await api.post<LmsAssignment>(`/api/lms/admin/assignments/${enc(assignmentId)}/unwaive`, body);
  return response.data;
}

/** POST /api/lms/admin/assignments/:id/cancel {reason} */
export async function cancelAssignment(assignmentId: string, reason: string): Promise<LmsAssignment> {
  const response = await api.post<LmsAssignment>(`/api/lms/admin/assignments/${enc(assignmentId)}/cancel`, { reason });
  return response.data;
}

// ---------------------------------------------------------------------------
// The admin's course list (GET /courses?all=1, 18.17)
// ---------------------------------------------------------------------------

/** One GET /courses?all=1 item: the learner item plus the admin fields (WP-30 reads.js). */
export interface AdminCourseItem {
  id: string;
  slug: string;
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  validity_months: number | null;
  estimated_minutes: number | null;
  /** null until a version is published (G8): nothing can be sent before then. */
  current_version: { id: string; version_no: number; published_at: string | null } | null;
  /** false for a retired course (8.1): it can be neither assigned nor required. */
  is_active: boolean;
  origin?: 'builtin' | 'custom';
  builtin_slug?: string | null;
  renewal_lead_days?: number;
  description?: string | null;
  draft_version?: { id: string; version_no: number } | null;
}

/** Appendix A: a list page holds at most 200 rows. */
const COURSE_PAGE = 200;
/** A guard against a server that never says hasMore: false (10 pages is 2000 courses). */
const COURSE_PAGE_LIMIT = 10;

/**
 * Every course of the network as an lms admin sees it (GET /courses?all=1,
 * paged to the end), in the server's title order.
 */
export async function listAdminCourses(options: { category?: CourseCategory | null } = {}): Promise<AdminCourseItem[]> {
  const out: AdminCourseItem[] = [];
  for (let pageNo = 0; pageNo < COURSE_PAGE_LIMIT; pageNo += 1) {
    const params: Record<string, string | number> = { all: 1, limit: COURSE_PAGE, offset: pageNo * COURSE_PAGE };
    if (options.category) params.category = options.category;
    // eslint-disable-next-line no-await-in-loop
    const response = await api.get<LmsList<AdminCourseItem>>('/api/lms/courses', { params });
    const items = Array.isArray(response.data?.items) ? response.data.items : [];
    out.push(...items);
    if (!response.data?.page?.hasMore) break;
  }
  return out;
}
